import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { UpdateDepositWithdrawalMasterTransaction } from '@/api/Transaction/Types/UpdateDepositWithdrawalMasterTransaction'
import { TransactionStateDto } from '@/api/Wallet/Types/TransactionStateDto'
import { NavigationHeader } from '@/components/NavigationHeader/NavigationHeader'
import { CompanyTag } from '@/components/ui/badge'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Field, FieldLabel } from '@/components/ui/field'
import {
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu'
import { Field, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { ViewTabs } from '@/components/ui/tabs'
import { NPaymentsApi } from '@/utils/api/nPaymentsApi'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { PageData } from 'api/Pagination/Types'
import { PaymentProviderSuccessValidation } from 'api/PaymentProvider/Types/PaymentProviderValidation'
import { BonusTransactionDto } from 'api/Transaction/Types/BonusTransactionDto'
import { RawTransactionFilterKey } from 'api/Transaction/Types/RawTransactionDto'
import {
  isTransactionNotConnectedToPspService,
  isTransactionPaymentProviderDetailsCategoryBank,
  isTransactionWalletTickmillCompanyUK,
  isTransactionWithdrawalType,
  TransactionDto,
} from 'api/Transaction/Types/Transaction'
import {
  TransactionsStatusReason,
  TransactionsStatusReasonType,
} from 'api/Transaction/Types/TransactionsStatusReason'
import { TransactionType } from 'api/Wallet/Types/TransactionTypeDto'
import { WalletDto } from 'api/Wallet/Types/Wallet'
import { AxiosError, isAxiosError } from 'axios'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { WarningOutlined } from 'components/Icon'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { useAppDispatch, useAppSelector } from 'store'
import { initialState } from 'store/grid'
import {
  fetchPaymentProviderAction,
  fetchTransactionReasonStatusAction,
  fetchTransactionStatusAction,
} from 'store/transactions/transaction.action'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientAreaApiClient } from 'utils/api/clientAreaApi'
import { PaymentApiClient } from 'utils/api/paymentApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { hasPermission } from 'utils/permissionsValidator'
import { buildQuery, FilterLogic } from 'utils/QueryHelpers'
import { sleep } from 'utils/sleep'
import {
  isTransactionCompletedStatus,
  isTransactionDeclinedStatus,
  isTransactionFailedStatus,
  TransactionStatus,
} from 'utils/transaction/status.index'
import { getCreditCreditOrFirstOneWallet } from 'utils/wallet'

import ClientAreaAuditLogModal from '../../../../components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import { AggregateDataTable } from '../AggregateDataTable'
import { TransactionDetailsTab } from '../DetailTabs'
import { LinkedTransactionTable } from '../LinkedTransactions'
import { PaymentProviderTransactionTable } from '../PaymentProviderTransactionTable'
import { TransactionDetailsLotsProfitStatistics } from '../TransactionDetailsLotsProfitStatistics/TransactionDetailsLotsProfitStatistics'
import TransactionDetailsNotesPage from '../TransactionDetailsNotes/TransactionDetailsNotesPage'
import TransactionHistoryDetails from '../TransactionHistoryDetails'
import { TransactionsStatisticOverview } from '../TransactionsStatisticOverview/TransactionsStatisticOverview'
import { TransactionStatusHeaderAction } from '../TransactionStatusHeaderAction'
import {
  CustomizeTransactionForm,
  CustomizeTransactionFormValues,
} from './CustomizeTransactionForm'
import { ProviderValidationForm, ProviderValidationFormValues } from './ProviderValidationForm'
import { TransactionClientBonuses } from './TransactionClientBonuses'
import { TransactionClientWallets } from './TransactionClientWallets'
import { TransactionDetailsWalletDepositWithdrawalGeneralPage } from './TransactionDetailsWalletDepositWithdrawalGeneralPage'

import styles from './TransactionDetailsWalletDepositWithdrawalPage.module.scss'

interface OuterProps {
  data: TransactionDto

  forceRefresh(): void
}

interface ModalDataProps {
  visible: boolean
  transaction?: TransactionDto
  statusId?: number
  statusReason?: TransactionsStatusReason
}

const useMapStateToProps = () => ({
  transactionState: useAppSelector((state) => state.transactions.status),
  statusReasons: useAppSelector((state) => state.transactions.statusReasons),
  paymentProviders: useAppSelector((state) => state.transactions.paymentProviders),
})

const useTransactionWalletFetch = (props: OuterProps) => {
  const { data } = props

  const dispatch = useAppDispatch()

  const walletsApiClient = useApiClient(WalletsApiClient)
  const paymentApiClient = useApiClient(PaymentApiClient)

  const { paymentProviders } = useMapStateToProps()

  const [wallets, setWallets] = useState<WalletDto[]>([])
  const [bonuses, setBonuses] = useState<PageData<BonusTransactionDto[]> | undefined>(undefined)
  const [client, setClient] = useState<{ id: string; name: string } | undefined>(undefined)
  const [transactionWalletId, setTransactionWalletId] = useState<string | undefined>()

  const { notes } = useTransactionNotesFetch()
  const { transactionState } = useTransactionStatusFetch(props)

  useEffect(() => {
    if (data) {
      const transactionWallet = getCreditCreditOrFirstOneWallet(data.walletTransactions)?.wallet
      if (transactionWallet) {
        if (isEmpty(wallets)) {
          getWalletsByClient(transactionWallet.client.id)
        }
        if (isEmpty(bonuses)) {
          getActiveBonusesByClient(transactionWallet.client.id)
        }
        setClient(transactionWallet.client)
        setTransactionWalletId(transactionWallet.id)
      }
      dispatch(fetchTransactionReasonStatusAction(walletsApiClient, data.type.id))
      if (isEmpty(paymentProviders)) {
        dispatch(fetchPaymentProviderAction(paymentApiClient))
      }
    }
  }, [data]) // eslint-disable-line react-hooks/exhaustive-deps

  const getWalletsByClient = async (clientId: string) => {
    const result = await walletsApiClient.getWalletsByClientId(clientId)
    setWallets(result)
  }

  const getActiveBonusesByClient = async (clientId: string) => {
    const query = buildQuery(createBonusTransactionsQuery(clientId).search)
    const result = await walletsApiClient.getBonusTransactions({
      search: query,
      pageSize: 100,
      pageIndex: 1,
    })
    setBonuses(result)
  }

  const createBonusTransactionsQuery = (clientId: string) => {
    const initialGridState = initialState()
    return {
      search: {
        ...initialGridState.filter,
        logic: initialGridState.filter.logic,
        defaultFilters: {
          logic: FilterLogic.AND,
          filters: [
            {
              field: 'Client',
              operator: '==',
              value: clientId,
            },
            {
              field: RawTransactionFilterKey.bonusTransactionStatusId,
              operator: '==',
              value: 10,
            },
          ],
        },
      },
    }
  }

  return { notes, transactionState, wallets, bonuses, client, transactionWalletId }
}

const useTransactionNotesFetch = () => {
  const { id } = useParams<{ id: string }>()

  const walletsApiClient = useApiClient(WalletsApiClient)

  const getTransactionNotes = useCallback(async () => {
    if (id) return await walletsApiClient.getTransactionNotesByTransactionId(id)
  }, [id]) // eslint-disable-line react-hooks/exhaustive-deps
  const { data: notes = [] } = useFetchOne(getTransactionNotes)

  return { notes }
}

interface TransactionStatusFetchProps {
  transactionState: TransactionStateDto[]
}

const useTransactionStatusFetch = (props: OuterProps): TransactionStatusFetchProps => {
  const { data } = props

  const { id: transactionId } = useParams<{ id: string }>()

  const dispatch = useAppDispatch()
  const walletsApiClient = useApiClient(WalletsApiClient)

  const { transactionState } = useMapStateToProps()

  const getTransactionNotes = useCallback(async () => {
    if (data) {
      if (isTransactionStateProcessingFlow(data) && transactionId) {
        return await walletsApiClient.getTransactionByIdStatus(transactionId)
      }

      if (isEmpty(transactionState)) {
        dispatch(fetchTransactionStatusAction(walletsApiClient))
      }
    }
  }, [transactionId, data]) // eslint-disable-line react-hooks/exhaustive-deps
  const { data: states } = useFetchOne(getTransactionNotes)

  return { transactionState: (states || transactionState || []) as TransactionStateDto[] }
}

const useTransactionWalletMutate = (
  props: OuterProps,
  transactionData: ReturnType<typeof useTransactionWalletFetch>,
  confirmationModal: ReturnType<typeof useConfirmationModal>,
  negativeBalanceModal: ReturnType<typeof useNegativeBalanceModal>
) => {
  const { data, forceRefresh } = props
  const { client } = transactionData

  const setToast = useContext(ToastContext)

  const walletsApiClient = useApiClient(WalletsApiClient)
  const clientAreaApiClient = useApiClient(ClientAreaApiClient)

  const [tempReason, setTempReason] = useState<TransactionsStatusReason | null>(null)
  const [reasonInput, setReasonInput] = useState<string | null>(null)

  const handleChangeStatus = async () => {
    const { statusReason, statusId, transaction } = confirmationModal.data

    try {
      if (!statusReason && statusId && transaction) {
        await walletsApiClient.updateTransactionStatus(statusId, { transactionId: transaction.id })
        setToast(successToast('Transaction status updated.'))
      }

      if (statusReason && transaction) {
        if (statusReason.description === 'Other') {
          setTempReason(statusReason)
          confirmationModal.hideConfirmationModal()
          return setReasonInput(' ')
        } else {
          await walletsApiClient.updateTransactionStatus(statusReason.transactionState.id, {
            transactionId: transaction.id,
            reason: null,
            transactionStateReasonId: statusReason.id,
          })
          setToast(successToast('Transaction status updated.'))
        }
      }

      confirmationModal.closeConfirmationModal()
      forceRefresh()
    } catch (error: unknown) {
      const err = error as AxiosError
      if (isAxiosError(err.response)) {
        if (
          err.response.response?.data.code === 'not_enough_trading_account_equity' ||
          err.response.response?.data.code === 'not_enough_trading_account_equities'
        ) {
          confirmationModal.closeConfirmationModal()
          negativeBalanceModal.openErrorModal()
        }
      }
      confirmationModal.closeConfirmationModal()
    }
  }

  const handleChangeStatusCustomReason = async () => {
    if (tempReason) {
      await walletsApiClient.updateTransactionStatus(tempReason?.transactionState.id, {
        transactionId: data!.id,
        reason: reasonInput?.trimLeft(),
        transactionStateReasonId: tempReason.id,
      })
      setToast(successToast('Transaction status updated.'))
      await sleep(2000)
      setReasonInput(null)
      forceRefresh()
    }
  }

  const handleConfirmationModalOpen = async (
    statusId: number,
    statusReason: TransactionsStatusReason | null
  ) => {
    if (data) {
      confirmationModal.openConfirmationModal({
        transaction: data,
        statusId,
        statusReason: statusReason || undefined,
      })
    }
  }

  const sendBankStatementNeededCCCoveredIBEmailRequest = async () => {
    if (client?.id) {
      await clientAreaApiClient.sendBankStatementNeededCCCoveredIBRequest(client.id)
      setToast(successToast('Email sent.'))
    }
  }

  const sendBankStatementNeededDepositConfirmationEmailRequest = async () => {
    if (client?.id) {
      await clientAreaApiClient.sendBankStatementNeededDepositConfirmationRequest(client.id)
      setToast(successToast('Email sent.'))
    }
  }

  return {
    confirmationModal,
    tempReason,
    reasonInput,
    setReasonInput,
    handleChangeStatus,
    handleChangeStatusCustomReason,
    handleConfirmationModalOpen,
    sendBankStatementNeededCCCoveredIBEmailRequest,
    sendBankStatementNeededDepositConfirmationEmailRequest,
  }
}

const useConfirmationModal = () => {
  const initialState = { visible: false }

  const [confirmationModal, setConfirmationModal] = useState<ModalDataProps>(initialState)

  const openConfirmationModal = (data: Omit<ModalDataProps, 'visible'>) => {
    setConfirmationModal({
      visible: true,
      transaction: data.transaction,
      statusId: data.statusId,
      statusReason: data.statusReason || undefined,
    })
  }

  const closeConfirmationModal = () => {
    setConfirmationModal({
      visible: false,
      statusId: undefined,
      transaction: undefined,
      statusReason: undefined,
    })
  }

  const hideConfirmationModal = () => {
    setConfirmationModal({ visible: false })
  }

  return {
    data: confirmationModal,
    setConfirmationModal,
    openConfirmationModal,
    closeConfirmationModal,
    hideConfirmationModal,
  }
}

const useNegativeBalanceModal = () => {
  const [negativeBalanceModal, setNegativeBalanceModal] = useState<ModalDataProps>({
    visible: false,
  })

  const openNegativeBalanceModal = () => {
    setNegativeBalanceModal({
      visible: true,
    })
  }

  const closeNegativeBalanceModal = () => {
    setNegativeBalanceModal({
      visible: false,
    })
  }

  return {
    data: negativeBalanceModal,
    openErrorModal: openNegativeBalanceModal,
    closeErrorModal: closeNegativeBalanceModal,
  }
}

const useProviderValidationModal = () => {
  const setToast = useContext(ToastContext)
  const nPaymentApiClient = useApiClient(NPaymentsApi)
  const [isProviderValidationModalOpen, setProviderValidationModalOpen] = useState<boolean>(false)
  const [paymentProviderSuccessValidationFields, setPaymentProviderSuccessValidationFields] =
    useState<PaymentProviderSuccessValidation | null>(null)

  const handleProviderValidationSubmit = async (values: ProviderValidationFormValues) => {
    try {
      setPaymentProviderSuccessValidationFields(null)
      const response = await nPaymentApiClient.sendForValidation(
        values.methodId,
        values.email,
        values.firstName,
        values.lastName
      )
      if (!response.result) {
        setToast(successToast(response?.paymentAccountInfo ?? 'The validation result is FALSE'))
        setPaymentProviderSuccessValidationFields({
          result: response.result,
          userEmail: response.userEmail,
          userFirstName: response.userFirstName,
          userLastName: response.userLastName,
          paymentAccountInfo: response?.paymentAccountInfo,
        })
      } else {
        setToast(successToast(response?.paymentAccountInfo ?? 'The validation result is TRUE'))
        setPaymentProviderSuccessValidationFields({
          result: response.result,
          userEmail: response.userEmail,
          userFirstName: response.userFirstName,
          userLastName: response.userLastName,
          paymentAccountInfo: response?.paymentAccountInfo,
        })
      }
    } catch (error) {
      console.error(error)
    }
  }

  return {
    isProviderValidationModalOpen,
    setProviderValidationModalOpen,
    paymentProviderSuccessValidationFields,
    handleProviderValidationSubmit,
  }
}

const useViewData = (
  props: OuterProps,
  transactionWalletData: ReturnType<typeof useTransactionWalletFetch>
) => {
  const { data } = props
  const { wallets, bonuses } = transactionWalletData

  const transactionWallet = getCreditCreditOrFirstOneWallet(data.walletTransactions)

  const isWalletsTabWarningVisible = useMemo(() => {
    if (!wallets.length) {
      return false
    }
    if (wallets.length) {
      let bonusReservedMax = 0
      let balanceMax = 0
      wallets.forEach((wallet) => {
        if (wallet.bonusReserved > bonusReservedMax) {
          bonusReservedMax = wallet.bonusReserved
        }
        if (wallet.balance > balanceMax) {
          balanceMax += wallet.balance
        }
      })
      return bonusReservedMax > balanceMax
    }
    return false
  }, [wallets])

  const isBonusesTabWarningVisible = useMemo(() => {
    return !!bonuses?.items?.length
  }, [bonuses])

  return { isWalletsTabWarningVisible, isBonusesTabWarningVisible, transactionWallet }
}

const TransactionDetailsWalletDepositWithdrawalPage: React.FC<OuterProps> = (props) => {
  const { data, forceRefresh } = props

  const [auth] = useContext(AuthSessionContext)

  const walletsApiClient = useApiClient(WalletsApiClient)
  const nPaymentsApi = useApiClient(NPaymentsApi)

  const permissions = auth?.permissions || []

  const [isShowHistoryModalOpen, setShowHistoryModalOpen] = useState<boolean>(false)
  const [auditLogVisible, setAuditLogVisible] = useState<boolean>(false)
  const [isCustomizeModalVisible, setCustomizeModalVisible] = useState<boolean>(false)
  const [customizeConfirmModal, setCustomizeConfirmModal] = useState<{
    visible: boolean
    values?: CustomizeTransactionFormValues
  }>({ visible: false })
  const providerValidationModal = useProviderValidationModal()
  const confirmationModal = useConfirmationModal()
  const negativeBalanceModal = useNegativeBalanceModal()

  const [activeTab, setActiveTab] = useState<string>(TransactionDetailsTab.GENERAL)

  const transactionWalletData = useTransactionWalletFetch(props)
  const { notes, transactionState, wallets, client, transactionWalletId } = transactionWalletData

  const transactionWalletMutate = useTransactionWalletMutate(
    props,
    transactionWalletData,
    confirmationModal,
    negativeBalanceModal
  )
  const { tempReason, reasonInput } = transactionWalletMutate
  const { handleChangeStatus, setReasonInput } = transactionWalletMutate
  const mutate = transactionWalletMutate

  const { isBonusesTabWarningVisible, isWalletsTabWarningVisible, transactionWallet } = useViewData(
    props,
    transactionWalletData
  )

  const setToast = useContext(ToastContext)

  const { callback: fetchAlternatives } = useCallbackWithForceRefresh(async () => {
    if (!transactionWallet) return []
    const pspId = transactionWallet.paymentProviderTransaction?.paymentProvider.id
    const currencyId = transactionWallet.otherCurrency?.id
    if (!pspId || !currencyId) return []
    return nPaymentsApi.getAlternativesPaymentProviders(pspId, currencyId)
  }, [transactionWallet])

  const { data: alternatives = [] } = useFetchOne(fetchAlternatives)

  const onCustomizeSubmit = async (values: CustomizeTransactionFormValues) => {
    const payload: UpdateDepositWithdrawalMasterTransaction = {
      transactionStateId: values.transactionStateId.id,
      paymentProviderMethodId:
        typeof values.paymentProviderMethodId === 'object'
          ? (values.paymentProviderMethodId as unknown as { id: string }).id
          : values.paymentProviderMethodId,
      walletTransactions: [
        {
          amount: values.amount,
          currencyRate: values.rate,
          otherAmount: values.originalAmount,
          otherCurrencyId: values.originalCurrency,
          wallet: {
            balanceDelta: values.balanceDelta,
            reservedDelta: values.reservedDelta,
            incomingReservedDelta: values.pendingDelta,
          },
        },
      ],
      comment: values.comment,
      reason: values.reason,
    }

    await walletsApiClient.updateDepositWithdrawalMasterTransaction(data.id, payload)
    setCustomizeModalVisible(false)
    setToast(successToast('Transaction updated successfully.'))
    forceRefresh()
  }

  const handleCustomizeSubmit = async (values: CustomizeTransactionFormValues): Promise<void> => {
    setCustomizeConfirmModal({ visible: true, values })
    setCustomizeModalVisible(false)
  }

  return (
    <React.Fragment>
      {reasonInput && tempReason && (
        <Dialog open={!!reasonInput} onOpenChange={() => setReasonInput(null)}>
          <DialogContent className='max-w-md'>
            <DialogHeader>
              <DialogTitle>Reason</DialogTitle>
            </DialogHeader>
            <div className='px-4 pb-4'>
              <Field>
                <FieldLabel>Reason</FieldLabel>
                <Input
                  onChange={(e) => setReasonInput(e.target.value)}
                  type='text'
                  value={reasonInput}
                />
              </Field>
              <div className='flex justify-center pt-4'>
                <Button type='button' onClick={mutate.handleChangeStatusCustomReason}>
                  Save
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
      {customizeConfirmModal.visible &&
        customizeConfirmModal.values &&
        (() => {
          const newValues = customizeConfirmModal.values
          const walletTransaction = data.walletTransactions[0]

          const fieldPairs: Array<{
            id: string
            label: string
            previousValue: string
            currentValue: string
          }> = [
            {
              id: 'status',
              label: 'Status:',
              previousValue: data.state.name || '',
              currentValue: newValues.transactionStateId.name,
            },
            {
              id: 'amount',
              label: 'Amount:',
              previousValue: walletTransaction.amount.toString() || '',
              currentValue: newValues.amount.toString() || '',
            },
            {
              id: 'rate',
              label: 'Rate:',
              previousValue: walletTransaction.currencyRate.toString() || '',
              currentValue: newValues.rate.toString() || '',
            },
            {
              id: 'originalAmount',
              label: 'Original Amount:',
              previousValue: walletTransaction.otherAmount.toString() || '',
              currentValue: newValues.originalAmount.toString() || '',
            },
            {
              id: 'paymentProvider',
              label: 'Payment Provider:',
              previousValue:
                alternatives.find(
                  (x) => x.id === walletTransaction.paymentProviderTransaction?.paymentProvider.id
                )?.name ?? '',
              currentValue:
                alternatives.find(
                  (x) =>
                    x.id ===
                    (typeof newValues.paymentProviderMethodId === 'object'
                      ? (newValues.paymentProviderMethodId as unknown as { id: string }).id
                      : newValues.paymentProviderMethodId)
                )?.name ?? '',
            },
            {
              id: 'comment',
              label: 'Comment:',
              previousValue: data.comment || '',
              currentValue: newValues.comment || '',
            },
            {
              id: 'reason',
              label: 'Reason:',
              previousValue: '',
              currentValue: newValues.reason || '',
            },
            {
              id: 'balanceDelta',
              label: 'Balance Delta:',
              previousValue: '0',
              currentValue: `${newValues.balanceDelta.toString()} ${transactionWallet?.otherCurrency?.id ?? ''}`,
            },
            {
              id: 'reservedDelta',
              label: 'Reserved Delta:',
              previousValue: '0',
              currentValue: `${newValues.reservedDelta.toString()} ${transactionWallet?.otherCurrency?.id ?? ''}`,
            },
            {
              id: 'pendingDelta',
              label: 'Pending Delta:',
              previousValue: '0',
              currentValue: `${newValues.pendingDelta.toString()} ${transactionWallet?.otherCurrency?.id ?? ''}`,
            },
          ]

          const changes = fieldPairs.filter(({ id, previousValue, currentValue }) => {
            const deltaFields = ['balanceDelta', 'reservedDelta', 'pendingDelta']

            if (deltaFields.includes(id)) {
              return previousValue !== currentValue.split(' ')[0]
            }

            return previousValue !== currentValue
          })

          const closeConfirmationModal = () => {
            setCustomizeConfirmModal({ visible: false })
            setCustomizeModalVisible(false)
          }

          if (!changes.length) {
            closeConfirmationModal()
            return null
          }

          return (
            <ConfirmationModal
              renderTitle={
                <>
                  <div>You are going to submit the following changes:</div>
                  <br />
                  <div>
                    {changes.map(({ label, previousValue, currentValue }) => (
                      <div key={label}>
                        <strong>{label}</strong> {previousValue && `${previousValue} → `}
                        {currentValue}
                      </div>
                    ))}
                  </div>
                </>
              }
              yesButtonText='Proceed'
              onYes={() => {
                onCustomizeSubmit(newValues)
                setCustomizeConfirmModal({ visible: false, values: undefined })
              }}
              onCancel={() =>
                setCustomizeConfirmModal({
                  visible: false,
                  values: undefined,
                })
              }
              visible
            />
          )
        })()}
      {isCustomizeModalVisible && (
        <Dialog open={isCustomizeModalVisible} onOpenChange={() => setCustomizeModalVisible(false)}>
          <DialogContent className='min-w-[35vw] max-w-150'>
            <DialogHeader>
              <DialogTitle>Customize Transaction</DialogTitle>
            </DialogHeader>

            <CustomizeTransactionForm
              transaction={data}
              alternativesPaymentProviders={alternatives.map((a) => ({
                id: a.id,
                name: a.name,
              }))}
              onSubmit={handleCustomizeSubmit}
              onCancel={() => setCustomizeModalVisible(false)}
            />
          </DialogContent>
        </Dialog>
      )}
      {isShowHistoryModalOpen && (
        <Dialog open={isShowHistoryModalOpen} onOpenChange={() => setShowHistoryModalOpen(false)}>
          <DialogContent className='max-w-100'>
            <TransactionHistoryDetails id={data.id} />
          </DialogContent>
        </Dialog>
      )}
      {auditLogVisible && (
        <ClientAreaAuditLogModal
          isVisible={auditLogVisible}
          onClose={() => setAuditLogVisible(false)}
          transactionId={data.id}
        />
      )}
      {confirmationModal?.data?.visible && (
        <ConfirmationModal
          contentText={`Updating a transaction's state while that is ${confirmationModal?.data?.transaction?.state.name} could result to issues. Are you sure you want to proceed?`}
          yesButtonText='Yes, proceed'
          onYes={handleChangeStatus}
          onCancel={() => confirmationModal.setConfirmationModal({ visible: false })}
          visible={confirmationModal?.data?.visible}
        />
      )}
      {negativeBalanceModal?.data?.visible && (
        <Dialog
          open={negativeBalanceModal?.data?.visible}
          onOpenChange={negativeBalanceModal.closeErrorModal}
        >
          <DialogContent className='max-w-125'>
            <DialogHeader>
              <DialogTitle>Negative Equity Check</DialogTitle>
            </DialogHeader>

            <div className={styles.textContainer}>
              This client has negative equity in one of the trading accounts.
            </div>
          </DialogContent>
        </Dialog>
      )}
      {providerValidationModal.isProviderValidationModalOpen && (
        <Dialog
          open={providerValidationModal.isProviderValidationModalOpen}
          onOpenChange={(open) => {
            if (!open) {
              providerValidationModal.setProviderValidationModalOpen(false)
            }
          }}
        >
          <DialogContent className='max-w-275'>
            <ProviderValidationForm
              firstName={transactionWallet?.paymentProviderTransaction?.request?.user_first_name}
              lastName={transactionWallet?.paymentProviderTransaction?.request?.user_last_name}
              email={
                transactionWallet?.paymentProviderTransaction?.response?.update?.externalAccount ??
                transactionWallet?.paymentProviderTransaction?.response?.external_account ??
                ''
              }
              methodId={transactionWallet?.paymentProviderTransaction?.paymentProvider.id}
              paymentProviderSuccessValidationFields={
                providerValidationModal.paymentProviderSuccessValidationFields
              }
              onSubmit={providerValidationModal.handleProviderValidationSubmit}
            />
          </DialogContent>
        </Dialog>
      )}
      <Breadcrumbs
        crumbs={[
          {
            title: 'Transactions - Deposit/Withdrawal',
            link: links.transactionDepositWithdrawal.url,
          },
          {
            title: data.referenceId,
            link: '/transactions/' + data.id,
          },
        ]}
      />
      <NavigationHeader>
        <span className='font-semibold'>{data.referenceId}</span>
        {data.groupReferences?.map((group) => (
          <CompanyTag id={group.type.id === 4100 ? 'AdditionalDeposit' : 'WithdrawAmountSplit'}>
            {group.name}
          </CompanyTag>
        ))}
        <TransactionStatusHeaderAction
          statusName={data.state.name}
          onStatusClick={() => setShowHistoryModalOpen(true)}
          menuDisabled={!hasPermission(permissions, PermissionsEnum.CanEditTransactionStatus)}
          menuContent={
            !isNotAllowedToChangeState(data.state.id) ? (
              <StatusMenu
                {...props}
                transactionState={transactionState}
                onChangeStatus={mutate.handleConfirmationModalOpen}
              />
            ) : undefined
          }
        />
      </NavigationHeader>
      <ViewTabs
        defaultKey={TransactionDetailsTab.GENERAL}
        activeTab={activeTab}
        onChange={(value) => setActiveTab(value)}
        tabs={[
          {
            key: TransactionDetailsTab.GENERAL,
            translateKey: 'General',
            visible: true,
            component: (
              <TransactionDetailsWalletDepositWithdrawalGeneralPage
                transaction={data}
                notes={notes}
                client={client}
                permissions={permissions}
                sendBankStatementNeededCCCoveredIBEmailRequest={
                  mutate.sendBankStatementNeededCCCoveredIBEmailRequest
                }
                sendBankStatementNeededDepositConfirmationEmailRequest={
                  mutate.sendBankStatementNeededDepositConfirmationEmailRequest
                }
                setAuditLogModal={setAuditLogVisible}
                setCustomizeModal={setCustomizeModalVisible}
                setProviderValidationModal={providerValidationModal.setProviderValidationModalOpen}
                setActiveTab={setActiveTab}
                forceRefresh={forceRefresh}
              />
            ),
          },
          {
            key: TransactionDetailsTab.FUNDING_SUMMARY,
            translateKey: <span>Funding Summary</span>,
            visible: true,
            component: <AggregateDataTable clientId={client?.id} showBankName={true} />,
          },
          {
            key: TransactionDetailsTab.FUNDING_DETAIL,
            translateKey: <span>Funding Details</span>,
            visible: true,
            component: <PaymentProviderTransactionTable clientId={client?.id} />,
          },
          {
            key: TransactionDetailsTab.STATISTICS,
            translateKey: <span>Trading Account Statistics</span>,
            visible: true,
            component: client && <TransactionsStatisticOverview client={client} />,
          },
          {
            key: TransactionDetailsTab.LOTS_PROFIT_STATS,
            translateKey: <span>Lots/Profit Statistics</span>,
            visible: true,
            component: (
              <TransactionDetailsLotsProfitStatistics
                clientId={client?.id}
                walletId={transactionWalletId}
              />
            ),
          },
          {
            key: TransactionDetailsTab.BONUS,
            translateKey: (
              <span>
                {isBonusesTabWarningVisible && <WarningOutlined style={{ color: 'red' }} />} Bonus
              </span>
            ),
            visible: true,
            component: client && <TransactionClientBonuses client={client} />,
          },
          {
            key: TransactionDetailsTab.WALLETS,
            translateKey: (
              <span>
                {isWalletsTabWarningVisible && <WarningOutlined style={{ color: 'red' }} />} Wallets
              </span>
            ),
            visible: true,
            component: client && <TransactionClientWallets wallets={wallets} />,
          },
          {
            key: TransactionDetailsTab.LINKS,
            translateKey: (
              <span>
                {(!!data.linkedTo.length || !!data.linkedFrom.length) && (
                  <WarningOutlined style={{ color: 'red' }} />
                )}{' '}
                Links
              </span>
            ),
            visible: true,
            component: <LinkedTransactionTable data={data} />,
          },
          {
            key: TransactionDetailsTab.NOTES,
            translateKey: <span>Notes</span>,
            visible: true,
            component: <TransactionDetailsNotesPage transaction={data} />,
          },
        ]}
      />
    </React.Fragment>
  )
}

const isNotAllowedToChangeState = (transactionStatus: TransactionStatus) => {
  return [TransactionStatus.Completed, TransactionStatus.Reversed].includes(transactionStatus)
}

interface StatusMenuProps extends OuterProps {
  transactionState: TransactionStateDto[]

  onChangeStatus(statusId: number, reason?: TransactionsStatusReason | null): Promise<void>
}

const StatusMenu: React.FC<StatusMenuProps> = (props) => {
  const { data, transactionState, onChangeStatus } = props

  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  const { statusReasons } = useMapStateToProps()

  const handleUpdateStatus = async (statusId: number, reason?: TransactionsStatusReason | null) => {
    await onChangeStatus(statusId, reason)
  }

  const isTransactionFailed = () => {
    return (
      (data && isTransactionFailedStatus(data.state.id)) ||
      (data && isTransactionDeclinedStatus(data.state.id))
    )
  }

  const isTransactionSubMenuStateDisabled = (x: TransactionStateDto) => {
    if (isTransactionStateProcessingFlow(data)) {
      return !x.enabled
    }

    return (
      !hasPermission(permissions, PermissionsEnum.CanEditTransactionStatus) ||
      x.id === TransactionStatus.AwaitingApproval ||
      x.id === TransactionStatus.Pending ||
      x.id === TransactionStatus.Reversed
    )
  }

  const isTransactionSubMenuItemStateDisabled = (x: TransactionStateDto) => {
    if (isTransactionStateProcessingFlow(data)) {
      return !x.enabled
    }

    return (
      x.id === TransactionStatus.AwaitingApproval ||
      x.id === TransactionStatus.Pending ||
      x.id === TransactionStatus.Reversed
    )
  }

  // If the transaction fails, we want to provide the option to set the status to "Completed" only.
  if (isTransactionFailed()) {
    const completed = transactionState.find((x) => isTransactionCompletedStatus(x.id))
    if (!completed) {
      return <div />
    }

    return (
      <DropdownMenuItem onSelect={() => handleUpdateStatus(completed.id)}>
        {completed.name}
      </DropdownMenuItem>
    )
  }

  return (
    <>
      {transactionState.map((x) => (
        <DropdownMenuSub key={x.id}>
          <DropdownMenuSubTrigger disabled={isTransactionSubMenuStateDisabled(x)}>
            {x.name}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuItem
              disabled={isTransactionSubMenuItemStateDisabled(x)}
              onSelect={() => handleUpdateStatus(x.id)}
            >
              Set without reason
            </DropdownMenuItem>
            {statusReasons
              .filter((reason) => x.id === reason.transactionState.id)
              .filter(
                (reason) =>
                  !(
                    data?.type.id === TransactionType.WalletDeposit &&
                    reason.id === TransactionsStatusReasonType.PriorityCardWithdrawal
                  )
              )
              .map((reason) => (
                <DropdownMenuItem
                  disabled={isTransactionSubMenuItemStateDisabled(x)}
                  onSelect={() => handleUpdateStatus(reason.id, reason)}
                  key={reason.name}
                >
                  {reason.description}
                </DropdownMenuItem>
              ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      ))}
    </>
  )
}

const isTransactionStateProcessingFlow = (data: TransactionDto) => {
  const transactionWallet = getCreditCreditOrFirstOneWallet(data.walletTransactions)
  return (
    isTransactionWithdrawalType(data) &&
    isTransactionPaymentProviderDetailsCategoryBank(transactionWallet) &&
    isTransactionWalletTickmillCompanyUK(transactionWallet) &&
    isTransactionNotConnectedToPspService(transactionWallet)
  )
}

export default TransactionDetailsWalletDepositWithdrawalPage
